import {
  CostBearerAnswer,
  PatientAnswer,
  PatientWebAppRecordChoice,
  PracticeFormAnswer,
  PracticeFormQuestion,
  PracticePatient,
  PracticePatientFormData,
  PracticeTreatmentContracts,
} from "@physiofit-digital/physiofit_types";
import { StateCreator } from "zustand";
import { getDepartmentsWithVisibleCostBearers } from "../../features/practiceForms/utils/costBearerContractUtils";
import { berlinCalendarDay } from "../../features/welcome/utils/formatRecordDate";
import { State } from "./mainStore";

export type FormType = "checkIn" | "onboarding" | "anamnesis" | "other";

export type WebAppScope = {
  practiceId: string;
  chainId: string;
  linkKey: string;
  /** The session came from a practice release (PF-6133): every exit ends on /release/done. */
  viaRelease?: true;
  /** The dashboard origin embedding a staff-fill session (PF-6402); done and end are exchanged with it. */
  /** The dashboard origin embedding a staff-fill session; done and end are exchanged with it. */
  dashboardOrigin?: string;
};

export type FormState = {
  practiceFormIdsArray: { id: string; index: number }[] | undefined;
  currentPracticeFormIndex: number;
  /** The link form this run began with: past the forms the record already submitted today, else 0. */
  runStartFormIndex: number;
  practicePatientId: string | undefined;
  practicePatient: PracticePatient | undefined;
  currentPracticePatientForm: PracticePatientFormData | undefined;
  /** Session state, so a reload continues on the same question. */
  currentQuestionIndex: number;
  userCompletedCurrentQuestion?: boolean;
  userAcceptedUsageConditions?: boolean;
  currentPracticeTreatmentContracts?: PracticeTreatmentContracts;
  currentCostBearerScreenStep?: number;
  currentPracticePatientCostBearerDetails?: CostBearerAnswer;
  completedFormWrites: { [key: string]: true };
  /**
   * Practice and chain of the web session, as the server derived them (PF-6132 SMS login, PF-6133 practice release),
   * and the link the session began with (`getLinkKey`).
   */
  webAppScope?: WebAppScope;
  /** The records the chooser offers; session state, so a logout takes the names with it. */
  webAppChoices?: PatientWebAppRecordChoice[];
  /** Set when „Ja, andere Person" left the record: the tab continues on the identity step, not in the chooser. */
  leftForOtherPerson?: boolean;
  /**
   * Ids the create callable answered 'created' for in this tab's session (not 'matched'): such a record holds
   * exactly what the patient just typed on the identity step, so "Persönliche Daten" saves a later change on it
   * directly instead of asking (PF-6134). Session state, cleared on logout.
   */
  webAppCreatedIds: string[];
  /**
   * The Berlin day on which each record submitted each form, on this device. Device state, kept through logout and a
   * new visit of the link, so that a completed link does not start over for the same record on the same day.
   */
  submittedFormsByRecord: { [practicePatientId: string]: { [practiceFormId: string]: string } };
  rememberSubmittedForm: (practicePatientId: string, practiceFormId: string) => void;
  leaveRecord: (forOtherPerson?: boolean) => void;
  rememberWebAppChoice: (choice: PatientWebAppRecordChoice) => void;
  rememberCreatedRecordId: (practicePatientId: string) => void;
  setWebAppScope: (webAppScope?: WebAppScope) => void;
  setWebAppChoices: (webAppChoices?: PatientWebAppRecordChoice[]) => void;
  setUserAcceptedUsageConditions: (accepted: boolean) => void;
  setPracticeFormIdsArray: (
    practiceFormIdsArray: { id: string; index: number }[] | undefined,
  ) => void;
  setCurrentPracticeFormIndex: (index: number) => void;
  startRunAt: (formIndex: number) => void;
  setPracticePatientId: (practicePatientId: string | undefined) => void;
  setPracticePatient: (patient: PracticePatient | undefined) => void;
  setCurrentPracticePatientForm: (
    form: PracticePatientFormData | undefined,
  ) => void;
  setCurrentQuestionIndex: (index: number) => void;
  setUserCompletedCurrentQuestion: (completed: boolean) => void;
  setUserAnswerToQuestion: (
    questionId?: string,
    patientAnswer?: Omit<PatientAnswer, "skipped" | "answerType">,
    answerId?: string,
    isSkip?: boolean,
  ) => void;
  setCurrentPracticeTreatmentContracts: (
    contracts: PracticeTreatmentContracts | undefined,
  ) => void;
  setCurrentCostBearerScreenStep: (step: number) => void;
  recordCompletedFormWrite: (key: string) => void;
  deleteUserAnswerToQuestion: (questionId: string, answerId: string) => void;
  resetCurrentFormState: () => void;
  setCurrentPracticePatientCostBearerDetails: (
    details: CostBearerAnswer,
  ) => void;
  resetCurrentFormStateWithoutUser: () => void;
  currentFormData: { [key: string]: any } | undefined;
};

const initialState = {
  practiceFormIdsArray: undefined,
  currentPracticeFormIndex: 0,
  runStartFormIndex: 0,
  practicePatientId: undefined,
  currentPracticePatientForm: undefined,
  currentQuestionIndex: 0,
  currentFormData: undefined,
  practicePatient: undefined,
  currentPracticeTreatmentContracts: undefined,
  currentCostBearerScreenStep: undefined,
  currentPracticePatientCostBearerDetails: undefined,
  completedFormWrites: {},
  webAppScope: undefined,
  webAppChoices: undefined,
  leftForOtherPerson: undefined,
  webAppCreatedIds: [],
  submittedFormsByRecord: {},
};

/** The chosen record and its form run: leaving the record inside the session clears exactly these. */
const recordState = {
  practicePatient: undefined,
  practicePatientId: undefined,
  currentPracticePatientForm: undefined,
  currentQuestionIndex: 0,
  currentPracticePatientCostBearerDetails: undefined,
  currentCostBearerScreenStep: undefined,
  currentPracticeFormIndex: 0,
  runStartFormIndex: 0,
};

const stateWithoutUser = {
  ...recordState,
  completedFormWrites: {},
  webAppScope: undefined,
  webAppChoices: undefined,
  leftForOtherPerson: undefined,
  webAppCreatedIds: [],
};

/** What a logout clears belongs to the tab's session; mainStore keeps these keys in sessionStorage. */
export const sessionStateKeys = Object.keys(stateWithoutUser);

export const createCurrentFormSlice: StateCreator<FormState> = (set) => ({
  ...initialState,
  setWebAppScope: (webAppScope?: WebAppScope) => set({ webAppScope }),
  setWebAppChoices: (webAppChoices?: PatientWebAppRecordChoice[]) => set({ webAppChoices }),
  // Installing the next form resets completedFormWrites (PF-4878).
  leaveRecord: (forOtherPerson?: boolean) => set({ ...recordState, leftForOtherPerson: forOtherPerson }),
  // The chooser then lists whoever this session added or corrected, newest first like the login's list.
  rememberWebAppChoice: (choice: PatientWebAppRecordChoice) =>
    set((state) => {
      if (state.webAppChoices) {
        state.webAppChoices = [
          choice,
          ...state.webAppChoices.filter((known) => known.practicePatientId !== choice.practicePatientId),
        ];
      }
      return state;
    }),
  // „Andere Person" just created this record: "Persönliche Daten" saves a later change on it without asking.
  rememberCreatedRecordId: (practicePatientId: string) =>
    set((state) => {
      state.webAppCreatedIds.push(practicePatientId);
      return state;
    }),
  setUserAcceptedUsageConditions: (accepted: boolean) =>
    set({ userAcceptedUsageConditions: accepted }),
  setPracticeFormIdsArray: (
    practiceFormIdsArray: { id: string; index: number }[] | undefined,
  ) => set({ practiceFormIdsArray }),
  setCurrentPracticeFormIndex: (index: number) =>
    set({ currentPracticeFormIndex: index }),
  startRunAt: (formIndex: number) => set({ currentPracticeFormIndex: formIndex, runStartFormIndex: formIndex }),
  setPracticePatientId: (practicePatientId: string | undefined) =>
    set({ practicePatientId }),
  setPracticePatient: (patient: PracticePatient | undefined) =>
    set({ practicePatient: patient }),
  // The record of completed writes and the question index live and die with the
  // form document, so that a form run starts over here and nowhere else (PF-4878).
  setCurrentPracticePatientForm: (form: PracticePatientFormData | undefined) =>
    set({
      currentPracticePatientForm: form,
      completedFormWrites: {},
      currentQuestionIndex: 0,
      currentCostBearerScreenStep: undefined,
    }),
  setCurrentQuestionIndex: (index: number) => set({ currentQuestionIndex: index }),
  setCurrentPracticeTreatmentContracts: (
    contracts: PracticeTreatmentContracts | undefined,
  ) => set({ currentPracticeTreatmentContracts: contracts }),
  setCurrentCostBearerScreenStep: (step: number) =>
    set({ currentCostBearerScreenStep: step }),
  recordCompletedFormWrite: (key: string) =>
    set((state) => {
      state.completedFormWrites[key] = true;
      return state;
    }),
  rememberSubmittedForm: (practicePatientId: string, practiceFormId: string) =>
    set((state) => {
      const today = berlinCalendarDay(new Date());
      for (const [recordId, forms] of Object.entries(state.submittedFormsByRecord)) {
        for (const [formId, day] of Object.entries(forms)) {
          if (day !== today) delete forms[formId];
        }
        if (Object.keys(forms).length === 0) delete state.submittedFormsByRecord[recordId];
      }
      state.submittedFormsByRecord[practicePatientId] = {
        ...state.submittedFormsByRecord[practicePatientId],
        [practiceFormId]: today,
      };
      return state;
    }),
  setUserAnswerToQuestion: (questionId, patientAnswer, answerId, isSkip) =>
    set((state) => {
      if (!state.currentPracticePatientForm) {
        console.error("No currentPracticePatientForm set in state.");
        return state;
      }
      if (!answerId || !questionId) {
        console.error("answerId or questionId is undefined.");
        return state;
      }

      const form = state.currentPracticePatientForm;
      if (!form) {
        console.error("No currentPracticePatientForm found.");
        return state;
      }

      const question = form.questions?.[questionId];
      if (!question) {
        console.error("Question not found in currentPracticePatientForm.");
        return state;
      }

      if (!patientAnswer && isSkip !== true) {
        console.error(
          "Neither patientAnswer nor isSkip is provided for setting answer.",
        );
        return state;
      }

      const newAnswer: PatientAnswer = {
        id: answerId,
        answerType: question.answer.type,
        skipped: isSkip === true,
        ...patientAnswer,
      };

      if (
        !state.currentPracticePatientForm.questions[questionId].patientAnswers
      ) {
        state.currentPracticePatientForm.questions[questionId].patientAnswers =
          {
            [answerId]: newAnswer,
          };
      }

      state.currentPracticePatientForm.questions[questionId].patientAnswers[
        answerId
      ] = newAnswer;

      return state;
    }),
  setUserCompletedCurrentQuestion: (completed: boolean) =>
    set({ userCompletedCurrentQuestion: completed }),
  deleteUserAnswerToQuestion: (questionId: string, answerId: string) =>
    set((state) => {
      const form = getCurrentPracticePatientForm(state);
      const questions = form?.questions;
      if (!questions || !form) {
        console.error("No currentPracticePatientForm set in state.");
        return state;
      }

      const currentQuestionAnswers =
        questions?.[questionId]?.patientAnswers ?? {};

      // const updatedAnswers = { ...currentQuestionAnswers };
      const currentQuestionAnswersArray = Object.entries(
        currentQuestionAnswers,
      );
      const newAnswersArray = currentQuestionAnswersArray.filter(
        ([id, _answer]) => id.startsWith(answerId) === false, // delete even the children's answers
      );

      const updatedAnswers: { [answerId: string]: PatientAnswer } = {};
      newAnswersArray.forEach(([id, answer]) => {
        updatedAnswers[id] = answer;
      });
      return {
        currentPracticePatientForm: {
          ...form,
          questions: {
            ...form.questions,
            [questionId]: {
              ...form.questions[questionId],
              patientAnswers: {
                ...updatedAnswers,
              },
            } as PracticeFormQuestion & {
              patientAnswers?: { [answerId: string]: PatientAnswer };
            },
          },
        },
      };
    }),
  // replace (not merge) — callers pass complete objects; merging would keep
  // stale keys like a previously selected contract forever (PF-5333)
  setCurrentPracticePatientCostBearerDetails: (details: CostBearerAnswer) =>
    set(() => ({
      currentPracticePatientCostBearerDetails: details,
    })),
  resetCurrentFormState: () => set(initialState),
  resetCurrentFormStateWithoutUser: () => set(stateWithoutUser),
});

export const getChainId = (state: State) => state.chainId;
export const getPracticeFormId = (state: FormState) =>
  state.practiceFormIdsArray?.[state.currentPracticeFormIndex ?? 0]?.id;
export const getPracticePatientId = (state: FormState) =>
  state.practicePatientId;
export const getWebAppScope = (state: FormState) => state.webAppScope;
/** Read it before any logout: the logout clears webAppScope, and with it the marker. */
export const getIsReleaseSession = (state: FormState) => state.webAppScope?.viaRelease === true;
export const getStaffFillDashboardOrigin = (state: FormState) => state.webAppScope?.dashboardOrigin;
export const getWebAppChoices = (state: FormState) => state.webAppChoices;
export const getLeftForOtherPerson = (state: FormState) => state.leftForOtherPerson;
export const getWebAppCreatedIds = (state: FormState) => state.webAppCreatedIds;
export const getCurrentPracticePatientForm = (state: FormState) =>
  state.currentPracticePatientForm;

export const getCurrentFormData = (state: FormState) => state.currentFormData;

export const getAllUserAnswers = (state: FormState) => {
  const questions = getCurrentPracticePatientFormQuestions(state);
  const allAnswers: { [answerId: string]: PatientAnswer } = {};
  if (questions) {
    Object.values(questions).forEach((question) => {
      if (question.patientAnswers) {
        Object.entries(question.patientAnswers).forEach(
          ([answerId, answer]) => {
            allAnswers[answerId] = answer;
          },
        );
      }
    });
  }
  return allAnswers;
};

export const getCurrentFormType = (state: FormState): FormType | undefined => {
  return state.currentPracticePatientForm?.formType;
};

export const getPracticePatient = (state: FormState) => state.practicePatient;

export const getPracticePatientPersonalInfo = (state: FormState) => {
  return state.practicePatient?.personalInfo;
};

export const getPracticePatientPhoneNumber = (state: FormState) => {
  return state.practicePatient?.phone?.phoneNumber;
};

export const getCurrentPracticePatientFormQuestions = (state: FormState) => {
  return getCurrentPracticePatientForm(state)?.questions;
};

export const getCurrentPracticePatientFormQuestionsSortedArray = (
  state: FormState,
) => {
  const questions = getCurrentPracticePatientFormQuestions(state);
  const practiceTreatmentContracts =
    getCurrentPracticeTreatmentContracts(state);
  const practiceHasVisibleCostBearers =
    getDepartmentsWithVisibleCostBearers(
      practiceTreatmentContracts?.practiceTreatmentContracts,
    ).length > 0;
  if (!questions) return [];
  return Object.values(questions)
    .sort((a, b) => (a.questionIndex || 0) - (b.questionIndex || 0))
    .filter((q) => {
      // const conditionsState = getConditionsState(state);
      // if (!conditionsState) return true;
      // if (conditionToShow.sex && conditionToShow.sex === "female") {
      //   return (
      //     conditionsState.isFemale === true ||
      //     conditionsState.isFemale === undefined
      //   );
      // } else if (conditionToShow.minAge) {
      //   return (
      //     conditionsState.age === undefined ||
      //     conditionsState.age >= conditionToShow.minAge
      //   );
      // } else if (conditionToShow.maxAge) {
      //   return (
      //     conditionsState.age === undefined ||
      //     conditionsState.age <= conditionToShow.maxAge
      //   );
      // }

      if (q.answer.type === "costBearer" && !practiceHasVisibleCostBearers) {
        return false;
      }

      // we check if the question is dependent on another question's answer.
      const dependentAnswerId = q.answerDependency?.answerId;
      const dependentFieldId = q.answerDependency?.dependentFieldId;
      if (!dependentAnswerId || !dependentFieldId) {
        return true;
      }

      const userAnswer = getUserAnswerToQuestion(state, dependentAnswerId);
      if (!userAnswer) {
        return false;
      }
      if (userAnswer.fieldIds?.some((f) => f.value === dependentFieldId)) {
        return true;
      }

      return false;
    });
};

export const getUserAnswerToQuestion = (state: FormState, answerId: string) => {
  const allAnswers = getAllUserAnswers(state);
  return allAnswers[answerId];
};

export const getAllUserAnswersToQuestion = (
  state: FormState,
  questionId: string,
) => {
  const questions = getCurrentPracticePatientFormQuestions(state);
  if (!questions) return {};
  return questions[questionId]?.patientAnswers || {};
};

export const getPatientAnswersForIds = (
  state: FormState,
  answerIds: string[],
) => {
  const answers: { [answerId: string]: PatientAnswer } = {};
  answerIds.forEach((id) => {
    const answer = getUserAnswerToQuestion(state, id);
    if (answer) {
      answers[id] = answer;
    }
  });
  return answers;
};

export const getCurrentPracticePatient = (state: FormState) =>
  state.practicePatient;

export const getUserCompletedCurrentQuestion = (state: FormState) =>
  state.userCompletedCurrentQuestion;

export const getDidUserCompleteCurrentQuestion = (
  state: FormState,
  questionId: string,
) => {
  const question = getCurrentPracticePatientFormQuestions(state)?.[questionId];
  if (!question) return false;
  const answer = question.answer;
  return isAnswerCompleted(state, answer);
};

// recursive helper function that goes through the possibleFields and their children (if any) to determine if the answer is completed
const isAnswerCompleted = (
  state: FormState,
  answer: PracticeFormAnswer,
): boolean => {
  const answerId = answer.answerId;
  const patientAnswer = getUserAnswerToQuestion(state, answerId);
  if (
    answer.type === "multipleChoice" ||
    answer.type === "singleChoice" ||
    answer.type === "yesNo"
  ) {
    // check for children as well
    const selectedFieldIds = patientAnswer?.fieldIds;
    if (!selectedFieldIds || selectedFieldIds.length === 0) {
      return false;
    }
    for (const fieldId of selectedFieldIds) {
      if (
        fieldId.isOther &&
        fieldId.otherText &&
        fieldId.otherText.trim().length > 0
      ) {
        return true;
      }
      const field = answer.possibleFields?.find(
        (f) => f.fieldId === fieldId.value,
      );
      if (!field) {
        return false;
      }
      if (field.children) {
        const isChildCompleted = isAnswerCompleted(state, field.children);
        if (!isChildCompleted) {
          return false;
        }
      }
      return true;
    }
  } else if (answer.type === "OwnTextAnswer") {
    return (
      !!patientAnswer &&
      !!patientAnswer.textValue &&
      patientAnswer.textValue.trim().length > 0
    );
  } else if (answer.type === "bodyArea") {
    return (
      !!patientAnswer &&
      !!patientAnswer.bodySelectionDetails &&
      Object.keys(patientAnswer.bodySelectionDetails).length > 0
    );
  } else if (answer.type === "scale") {
    return (
      !!patientAnswer &&
      !!patientAnswer.textValue &&
      patientAnswer.textValue.trim().length > 0
    );
  } else if (
    answer.type === "documentUpload" ||
    answer.type === "recipeUpload"
  ) {
    return (
      !!patientAnswer &&
      !!patientAnswer.documentUploadDetails &&
      patientAnswer.documentUploadDetails.length > 0
    );
  } else if (answer.type === "contract") {
    return (
      !!patientAnswer &&
      !!patientAnswer.contractDetails &&
      !!patientAnswer.contractDetails.signedAt
    );
  } else if (answer.type === "conditionAcceptance") {
    return true;
  } else if (answer.type === "personalInfo") {
    const details = patientAnswer?.personalInfoDetails;
    const noAddress = answer.options?.includes("noAddress");
    const requiredFieldsFilled =
      details &&
      !!details.firstName &&
      !!details.lastName &&
      !!details.birthday &&
      (noAddress ||
        (!!details.address &&
          !!details.address.street &&
          !!details.address.zip &&
          !!details.address.city));
    return requiredFieldsFilled ?? false;
  } else if (answer.type === "costBearer") {
    const costBearerDetails = state.currentPracticePatientCostBearerDetails;
    const costBearerCurrentStep = state.currentCostBearerScreenStep;
    if (costBearerCurrentStep === 0) {
      return !!costBearerDetails?.departmentId;
    } else if (costBearerCurrentStep === 1) {
      return !!costBearerDetails?.costBearerId;
    } else if (costBearerCurrentStep === 2) {
      return !!costBearerDetails?.termsAccepted;
    } else {
      return true;
    }
  } else {
    return false;
  }
  return false;
};

export const getCurrentPracticeTreatmentContracts = (state: FormState) =>
  state.currentPracticeTreatmentContracts;

export const getCompletedFormWrites = (state: FormState) =>
  state.completedFormWrites;

export const getCurrentCostBearerScreenStep = (state: FormState) =>
  state.currentCostBearerScreenStep;

export const getCurrentQuestionIndex = (state: FormState) =>
  state.currentQuestionIndex;

export const getCurrentPracticePatientCostBearerDetails = (state: FormState) =>
  state.currentPracticePatientCostBearerDetails;

export const getCurrentPracticeFormIndex = (state: FormState) =>
  state.currentPracticeFormIndex;

export const getRunStartFormIndex = (state: FormState) => state.runStartFormIndex;

export const getPracticeFormIdsArray = (state: FormState) =>
  state.practiceFormIdsArray;

export const getCurrentFormIsLastPracticeForm = (state: FormState) => {
  const practiceFormIdsArray = state.practiceFormIdsArray;
  const currentIndex = state.currentPracticeFormIndex;
  if (!practiceFormIdsArray || currentIndex === undefined) return false;
  return currentIndex >= practiceFormIdsArray.length - 1;
};

/** The first form from the current one on that the record has not submitted today in Berlin, or -1 when none is left. */
export const getFirstOpenFormIndex = (state: FormState): number => {
  const { practiceFormIdsArray, practicePatientId, currentPracticeFormIndex } = state;
  if (!practiceFormIdsArray || !practicePatientId) return currentPracticeFormIndex;
  const today = berlinCalendarDay(new Date());
  const submitted = state.submittedFormsByRecord[practicePatientId];
  for (let index = currentPracticeFormIndex; index < practiceFormIdsArray.length; index++) {
    if (submitted?.[practiceFormIdsArray[index].id] !== today) return index;
  }
  return -1;
};
